import { useEffect, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import { ToggleGroup } from '@repo/videa-components/src/ui/toggle-group';
import { useQueryClient } from '@tanstack/react-query';
import { useAtom } from 'jotai';

import { endpoints } from '../../../react-query/endpoints';
import {
  usePreferences,
  usePreferencesPracticeUpdate,
} from '../../../react-query/usePreferences';
import { mixpanelEvents, useTrackEvent } from '../../../utils/mixpanel';
import { binFilterAtom } from '../findingSidebar/confidenceScoreFilter/atoms';

import { ConfidenceOption } from './ConfidenceOption';

export const ConfidencePreferenceV2 = (props: {
  setConfidencePrefOpen?: (value: boolean) => void;
  isEmbedded?: boolean;
}) => {
  const { setConfidencePrefOpen, isEmbedded } = props;
  const [binFilter, setBinFilter] = useAtom(binFilterAtom);
  const [selectedBin, setSelectedBin] = useState(binFilter);

  const { trackEvent } = useTrackEvent();
  const { data } = usePreferences();
  const queryClient = useQueryClient();

  const { mutate } = usePreferencesPracticeUpdate({
    onSuccess: () => {
      queryClient.invalidateQueries([endpoints.patientAnalysesPreview]);
      queryClient.invalidateQueries([endpoints.appointments]);
    },
  });

  useEffect(() => {
    if (data?.binThreshold !== undefined && data.binThreshold !== selectedBin) {
      setSelectedBin(data.binThreshold);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- React Hook useMemo has a missing dependency: 'selectedBin'. Either include it or remove the dependency array. */
  }, [data?.binThreshold]);

  const handleBinSelect = (value: number | undefined) => {
    setSelectedBin(value as 0 | 1 | 2 | undefined);
  };

  const onChangeCommitted = () => {
    setBinFilter(selectedBin);
    mutate({
      binThreshold: selectedBin,
    });
    trackEvent(mixpanelEvents.visitBinThresholdChanged, {
      value: selectedBin,
      isFromOnboarding: isEmbedded,
    });
    if (setConfidencePrefOpen) {
      setConfidencePrefOpen(false);
    }
  };

  return (
    <>
      <div className="vai-flex vai-justify-center vai-items-center vai-flex-nowrap vai-gap-4 vai-mb-6">
        <div className="vai-w-1/2 vai-flex vai-justify-center">
          <ToggleGroup
            type="single"
            value={selectedBin?.toString()}
            disabled={!data}
            className="vai-flex ai-h-full vai-flex-col vai-border-2 vai-border-blue-200 vai-rounded-2xl"
          >
            <ConfidenceOption
              value={0}
              title={<Trans>LESS</Trans>}
              description={
                <Trans>
                  Includes only very clear decay, provides more focused
                  detections
                </Trans>
              }
              selectedBin={selectedBin}
              onSelect={handleBinSelect}
            />
            <ConfidenceOption
              value={1}
              title={<Trans>BALANCED</Trans>}
              description={
                <Trans>
                  Includes moderate and advanced decay, highlights incipient
                  decay with clinical intervention potential
                </Trans>
              }
              selectedBin={selectedBin}
              onSelect={handleBinSelect}
            />
            <ConfidenceOption
              value={2}
              title={<Trans>MORE</Trans>}
              description={
                <Trans>
                  Includes the most incipient decay, reveals other potential
                  areas of interest that may not require immediate action
                </Trans>
              }
              selectedBin={selectedBin}
              onSelect={handleBinSelect}
            />
          </ToggleGroup>
        </div>
        <div className="vai-w-2/3 vai-flex vai-justify-center">
          <img
            src={
              selectedBin === 2
                ? '/assets/images/onboarding/caries3/more-detections.png'
                : selectedBin === 1
                  ? '/assets/images/onboarding/caries3/balanced-detections.png'
                  : selectedBin === 0
                    ? '/assets/images/onboarding/caries3/selective-detections.png'
                    : '/assets/images/onboarding/caries3/more-detections.png'
            }
            alt="Confidence Score Preview"
          />
        </div>
      </div>
      <Button onClick={onChangeCommitted} className="vai-mx-auto">
        <Trans>Confirm Preference</Trans>
      </Button>
    </>
  );
};
